/* eslint-disable indent, react/jsx-indent, react/jsx-indent-props, react/jsx-closing-tag-location */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
    Card, Space, Typography, Input, Button, Badge, Modal, Spin, Alert,
} from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import request from '../../../logic/utils/request';

function Allert() {
    const [messageType, setMessageType] = useState('Внимание!');
    const [messageText, setMessageText] = useState('Важное сообщение для посетителей');
    const [loadingActivate, setLoadingActivate] = useState(false);
    const [loadingDeactivate, setLoadingDeactivate] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);

    const fetchStatus = async () => {
        try {
            const res = await axios.post('/server/public/globalWarningStatus', { data: {} });
            const payload = res?.data;

            const statusTrue = payload?.status === 'true' || payload?.status === true;

            if (statusTrue) {
                setIsActive(true);
                // Загружаем текст из сервера, если он есть
                if (payload?.data) {
                    const newType = payload.data.message_type || 'Внимание!';
                    const newText = payload.data.message_text || 'Важное сообщение для посетителей';
                    setMessageType(newType);
                    setMessageText(newText);
                }
            } else {
                setIsActive(false);
                // Если алерт выключен, загружаем параметры по умолчанию
                try {
                    const newParams = await request('/server/api/officeparams/', 'get');
                    const { data: { data: paramsData } } = newParams;
                    paramsData.forEach((el) => {
                        if (el.param === 'alert_title_default') {
                            setMessageType(el.value || 'Внимание!');
                        }
                        if (el.param === 'alert_message_default') {
                            setMessageText(el.value || 'Важное сообщение для посетителей');
                        }
                    });
                } catch (e) {
                    // Если не удалось загрузить параметры, оставляем значения по умолчанию
                }
            }
            setLastUpdated(new Date());
        } catch (e) {
            // тихо игнорируем, чтобы не спамить тостами каждые 10 сек при сетевых проблемах
        } finally {
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        const timer = setInterval(fetchStatus, 10000);
        return () => clearInterval(timer);
    }, []);

    const doActivate = async () => {
        try {
            setLoadingActivate(true);
            const res = await request('/server/api/globalwarningmode/', 'activate', {
                message_type: messageType,
                message_text: messageText,
            });
            // request возвращает axios response, данные в res.data
            if (res?.data) {
                toast.success('Режим тревоги активирован');
                // Перезапрашиваем статус с сервера
                await fetchStatus();
            }
        } catch (e) {
            toast.error('Не удалось включить режим тревоги');
        } finally {
            setLoadingActivate(false);
        }
    };

    const activate = () => {
        Modal.confirm({
            title: 'Включить режим тревоги?',
            icon: <ExclamationCircleOutlined />,
            content: 'Сообщение будет показано на подключённых клиентах. Подтвердите действие.',
            okText: 'Включить',
            cancelText: 'Отмена',
            onOk: () => doActivate(),
        });
    };

    const doDeactivate = async () => {
        try {
            setLoadingDeactivate(true);
            const res = await request('/server/api/globalwarningmode/', 'deactivate', {});
            // request возвращает axios response, данные в res.data
            if (res?.data) {
                toast.success('Режим тревоги отключен');
                // Перезапрашиваем статус с сервера
                await fetchStatus();
            }
        } catch (e) {
            toast.error('Не удалось отключить режим тревоги');
        } finally {
            setLoadingDeactivate(false);
        }
    };

    const deactivate = () => {
        Modal.confirm({
            title: 'Отключить режим тревоги?',
            icon: <ExclamationCircleOutlined />,
            okText: 'Отключить',
            cancelText: 'Отмена',
            onOk: () => doDeactivate(),
        });
    };

    if (initialLoading) {
        return (
            <div style={{ padding: 16, maxWidth: 900 }}>
                <Card>
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Typography.Text type="secondary">Загрузка данных...</Typography.Text>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={{ padding: 16, maxWidth: 900 }}>
            <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography.Title level={3} style={{ margin: 0 }}>
                        Оповещение (режим тревоги)
                    </Typography.Title>
                    <Badge
                        status={isActive ? 'processing' : 'default'}
                        text={isActive ? 'Активен' : 'Выключен'}
                        style={{ fontSize: 16 }}
                    />
                </Space>

                {isActive && (
                    <Alert
                        key={`alert-${isActive}-${lastUpdated?.getTime()}`}
                        type="warning"
                        message="Режим тревоги активен"
                        description="Оповещение отображается на всех подключённых терминалах и главных табло."
                        showIcon
                    />
                )}

                <Card title="Параметры сообщения" size="small">
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <div>
                            <Typography.Text strong>Заголовок</Typography.Text>
                            <Input
                                value={messageType}
                                onChange={(e) => setMessageType(e.target.value)}
                                placeholder="Внимание!"
                                size="large"
                                style={{ marginTop: 8 }}
                            />
                        </div>
                        <div>
                            <Typography.Text strong>Текст сообщения</Typography.Text>
                            <Input.TextArea
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                placeholder="Ракетная опасность!"
                                autoSize={{ minRows: 4, maxRows: 8 }}
                                size="large"
                                style={{ marginTop: 8 }}
                            />
                        </div>
                    </Space>
                </Card>

                <Card title="Предпросмотр" size="small">
                    <div
                        aria-live="polite"
                        role="region"
                        aria-label="Предпросмотр оповещения"
                        style={{
                            position: 'relative',
                            border: '2px solid #ff4d4f',
                            background: '#fff1f0',
                            color: '#a8071a',
                            padding: '20px 20px 20px 70px',
                            borderRadius: 8,
                            minHeight: 100,
                        }}
                    >
                        <WarningOutlined style={{
                            position: 'absolute',
                            left: 20,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: 32,
                            color: '#a8071a',
                        }}
                        />
                        <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 20 }}>
                            {messageType || 'Заголовок сообщения'}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: 16, lineHeight: 1.5 }}>
                            {messageText || 'Текст сообщения'}
                        </div>
                    </div>
                </Card>

                <Space size={12}>
                    <Button
                        type="primary"
                        size="large"
                        onClick={activate}
                        loading={loadingActivate}
                        disabled={loadingActivate || loadingDeactivate}
                        icon={<WarningOutlined />}
                    >
                        {isActive ? 'Обновить оповещение' : 'Включить оповещение'}
                    </Button>
                    <Button
                        danger
                        size="large"
                        onClick={deactivate}
                        loading={loadingDeactivate}
                        disabled={!isActive || loadingDeactivate || loadingActivate}
                    >
                        Выключить оповещение
                    </Button>
                </Space>

                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {lastUpdated && `Обновлено: ${lastUpdated.toLocaleTimeString()}`}
                    {' · Автообновление каждые 10 секунд'}
                </Typography.Text>
            </Space>
        </div>
    );
}

export default Allert;
